/**
 * Account & Settings page.
 *
 * Shows the user's account info, Telegram bot connection instructions,
 * and a sign-out button. Accessible via the user email link in the Header.
 */

import { Link } from "react-router-dom";
import Header from "../components/Header.jsx";
import TenantSetupSection from "../components/TenantSetupSection.jsx";
import { getUserInfo, isAdmin, logout } from "../services/auth.js";
import { useStore } from "../store/useStore.js";

const TELEGRAM_BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "";

export default function Settings() {
  const user = getUserInfo();
  const admin = isAdmin();
  const tokenUsage = useStore((s) => s.tokenUsage);
  const loadUsage = useStore((s) => s.loadUsage);

  // Load usage on mount if not already loaded
  if (!tokenUsage) loadUsage();

  return (
    <>
      <Header />
      <main className="settings-page">
        <div className="settings-container">
          <h1>Settings</h1>

          {/* Account info */}
          <section className="settings-section">
            <h2>Account</h2>
            <div className="settings-card">
              <div className="settings-row">
                <span className="settings-label">Email</span>
                <span className="settings-value">{user?.email || "Unknown"}</span>
              </div>
              {tokenUsage && (
                <div className="settings-row">
                  <span className="settings-label">Usage today</span>
                  <span className="settings-value">
                    {tokenUsage.tokensUsedToday?.toLocaleString() || 0} / {tokenUsage.dailyTokenBudget?.toLocaleString() || "100,000"} tokens
                  </span>
                </div>
              )}
            </div>
          </section>

          {/* Telegram connection */}
          <section className="settings-section">
            <h2>Telegram</h2>
            <div className="settings-card">
              <p className="settings-description">
                Access your AI coaches from Telegram. Chat with personas, run roundtables,
                and upload documents -- all from your phone.
              </p>
              <div className="settings-steps">
                <div className="settings-step">
                  <span className="settings-step-number">1</span>
                  <div>
                    <strong>Open the bot</strong>
                    {TELEGRAM_BOT_USERNAME ? (
                      <p>
                        Search for{" "}
                        <a
                          href={`https://t.me/${TELEGRAM_BOT_USERNAME}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="settings-link"
                        >
                          @{TELEGRAM_BOT_USERNAME}
                        </a>{" "}
                        on Telegram, or tap the link.
                      </p>
                    ) : (
                      <p>Search for the AI Coach bot on Telegram.</p>
                    )}
                  </div>
                </div>
                <div className="settings-step">
                  <span className="settings-step-number">2</span>
                  <div>
                    <strong>Link your account</strong>
                    <p>
                      Send <code>/link {user?.email || "your@email.com"}</code> to connect
                      your Telegram to this account.
                    </p>
                  </div>
                </div>
                <div className="settings-step">
                  <span className="settings-step-number">3</span>
                  <div>
                    <strong>Start coaching</strong>
                    <p>
                      Use <code>/coaches</code> to see available personas,
                      then <code>/coach bezos</code> to begin.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Phase 44.8 — visible only to tenant-admin / super-admin;
              surfaces current tenant status + Re-open affordance when
              the tenant is active. Hidden for members. */}
          <TenantSetupSection />

          {admin && (
            <section className="settings-section">
              <h2>Admin</h2>
              <div className="settings-card">
                <p className="settings-description">
                  You have admin access. Manage personas, pipeline prompts,
                  usage, and guardrails from the admin panel.
                </p>
                <Link to="/admin" className="btn btn-ghost">
                  Open admin panel
                </Link>
              </div>
            </section>
          )}

          {/* Sign out */}
          <section className="settings-section">
            <button type="button" className="btn btn-ghost" onClick={logout}>
              Sign out
            </button>
          </section>
        </div>
      </main>
    </>
  );
}
